import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, getToken, setToken } from "../lib/api";

const AuthCtx = createContext(null);
const PREVIEW_KEY = "xpattern.previewAs";

const days = (n) => new Date(Date.now() + n * 86400000).toISOString();
// What the Owner sees when "viewing as a customer" - only the plan/role fields change, the account & plotter link stay real.
export const PREVIEW_PRESETS = {
  first_launch: { label: "1 · First launch — choose a plan (signed out)", signedOut: true },
  trial: { label: "2 · Free trial · 2 days left", patch: { subscription_status: "trial", plan_state: "trial", plan: null, billing: null, entitled: true, trial_days_left: 2, days_left: 2, trial_ends_at: days(2), renews_at: null } },
  trial_last_day: { label: "3 · Free trial · last day", patch: { subscription_status: "trial", plan_state: "trial", plan: null, billing: null, entitled: true, trial_days_left: 1, days_left: 1, trial_ends_at: days(1), renews_at: null } },
  expired: { label: "4 · Trial ended (locked)", patch: { subscription_status: "trial", plan_state: "expired", plan: null, billing: null, entitled: false, trial_days_left: 0, days_left: 0, trial_ends_at: days(-1), renews_at: null } },
  pending: { label: "5 · Paid plan chosen — payment not completed", patch: { subscription_status: "pending_payment", plan_state: "pending_payment", plan: null, plan_intent: "monthly", billing: null, entitled: false, trial_days_left: 0, days_left: 0, trial_ends_at: null, renews_at: null } },
  monthly: { label: "6 · Monthly subscriber", patch: { subscription_status: "active", plan_state: "active", plan: "monthly", billing: "autopay", entitled: true, trial_days_left: 0, days_left: 23, renews_at: days(23) } },
  yearly: { label: "7 · Yearly subscriber", patch: { subscription_status: "active", plan_state: "active", plan: "yearly", billing: "yearly", entitled: true, trial_days_left: 0, days_left: 300, renews_at: days(300) } },
  demo: { label: "8 · Demo · 7-day full access (welcome pop-up)", patch: { name: "Austin Demo", shop_name: "Austin's demo", demo: true, subscription_status: "trial", plan_state: "trial", plan: null, billing: null, entitled: true, trial_days_left: 7, days_left: 7, trial_ends_at: days(7), renews_at: null } },
  past_due: { label: "10 · Auto-pay failed (locked out)", patch: { subscription_status: "past_due", plan_state: "past_due", plan: "monthly", billing: "autopay", entitled: false, renewal_required: true, trial_days_left: 0, days_left: 0, trial_ends_at: null, renews_at: null } },
  demo_ended: { label: "9 · Demo ended (deactivated)", patch: { name: "Austin Demo", shop_name: "Austin's demo", demo: true, demo_ended: true, suspended: true, subscription_status: "trial", plan_state: "suspended", plan: null, billing: null, entitled: false, trial_days_left: 0, days_left: 0, trial_ends_at: days(-1), renews_at: null } },
};

const PREVIEW_HOST = /\.emergentagent\.com$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);
const isPreview = () => process.env.REACT_APP_PREVIEW_MODE === "true" && PREVIEW_HOST;

export const AuthProvider = ({ children }) => {
  const [realUser, setUser] = useState(null); // null = checking, false = signed out
  const [previewAs, setPreviewState] = useState(() => localStorage.getItem(PREVIEW_KEY) || null);

  const refresh = useCallback(async () => {
    try { setUser(await api.me()); return; } catch { /* access token gone or expired — try the 90-day refresh cookie before giving up */ }
    try {
      const data = await api.refreshSession();
      setToken(data.access_token);
      setUser(await api.me());
      return;
    } catch { /* not signed in */ }
    if (isPreview() && window.location.pathname !== "/start" && !window.location.pathname.startsWith("/demo/")) {
      try { const d = await api.previewLogin(); setToken(d.access_token); setUser(d.user); return; } catch { /* fall through */ }
    }
    setUser(false);
  }, []);

  useEffect(() => {
    refresh();
    const out = (e) => { setUser(false); if (typeof e?.detail === "string" && /another device|one active session/i.test(e.detail)) toast.warning(e.detail, { duration: 12000 }); };
    window.addEventListener("xpattern:signed-out", out);
    return () => window.removeEventListener("xpattern:signed-out", out);
  }, [refresh]);

  useEffect(() => {
    if (!realUser) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      api.ping().then((r) => {  // an early-access unlock (owner marked this shop's PPF request added) shows up without a reload
        const next = r?.unlocked_services || [];
        setUser((u) => {
          if (!u || JSON.stringify(u.unlocked_services || []) === JSON.stringify(next)) return u;
          const added = next.filter((s) => !(u.unlocked_services || []).includes(s));
          if (added.length) toast.success(`${added.join(", ")} is now unlocked for your shop`, { description: "Open the service tabs to start using it.", duration: 10000 });
          return { ...u, unlocked_services: next };
        });
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [realUser]);

  const setPreviewAs = (key) => {
    if (key) localStorage.setItem(PREVIEW_KEY, key); else localStorage.removeItem(PREVIEW_KEY);
    if (key === "demo") { localStorage.setItem("xpattern.welcomePending", "demo"); window.dispatchEvent(new Event("xpattern:welcome")); }
    setPreviewState(key || null);
  };
  const previewing = Boolean(realUser && realUser.role === "admin" && previewAs && PREVIEW_PRESETS[previewAs]);
  const user = useMemo(() => (previewing && PREVIEW_PRESETS[previewAs].signedOut ? false : previewing
    ? { ...realUser, role: "shop", name: "Preview Customer", shop_name: realUser.shop_name || "Demo Tint Shop", trial_days: 7, renewal_required: false, trial_locked: null, ...PREVIEW_PRESETS[previewAs].patch }
    : realUser), [realUser, previewing, previewAs]);
  const previewKeys = Object.keys(PREVIEW_PRESETS);
  const nextPreview = () => setPreviewAs(previewKeys[(previewKeys.indexOf(previewAs) + 1) % previewKeys.length]);

  useEffect(() => {
    const uid = realUser?.id;
    if (!uid) return;
    if (localStorage.getItem("xpattern.connection.uid") !== uid) { localStorage.removeItem("xpattern.connection"); localStorage.setItem("xpattern.connection.uid", uid); }
  }, [realUser?.id]);
  const signIn = (data) => { setToken(data.access_token); setUser(data.user); };
  // Owner "View as customer": park the owner token, run the workspace on the customer's token, full reload so every store starts as them.
  const viewAs = async (customerId) => {
    const r = await api.adminImpersonate(customerId);
    localStorage.setItem("xpattern.owner_token", getToken() || "");
    localStorage.setItem("xpattern.owner_connection", JSON.stringify({ c: localStorage.getItem("xpattern.connection"), uid: localStorage.getItem("xpattern.connection.uid") }));
    setToken(r.access_token);
    window.location.assign("/");
  };
  const exitViewAs = () => {
    const t = localStorage.getItem("xpattern.owner_token");
    const oc = JSON.parse(localStorage.getItem("xpattern.owner_connection") || "null");
    localStorage.removeItem("xpattern.owner_token"); localStorage.removeItem("xpattern.owner_connection");
    if (oc) { oc.c ? localStorage.setItem("xpattern.connection", oc.c) : localStorage.removeItem("xpattern.connection"); oc.uid ? localStorage.setItem("xpattern.connection.uid", oc.uid) : localStorage.removeItem("xpattern.connection.uid"); }
    setToken(t || null);
    window.location.assign("/");
  };
  const signOut = async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setPreviewAs(null);
    setToken(null); setUser(false);
    if (isPreview() && window.location.pathname !== "/start") window.location.assign("/start");
  };

  return <AuthCtx.Provider value={{ user, realUser, previewAs: previewing ? previewAs : null, setPreviewAs, nextPreview, signIn, signOut, refresh, viewAs, exitViewAs }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
